<script lang="ts" context="module">
    import { iconStateComponentIconMapping } from '@amp/web-app-components/src/components/buttons/PlayButton/constants';
    import type {
        IconState,
        PlayIconSet,
        IconMapping,
        IconStateCollection,
    } from '@amp/web-app-components/src/components/buttons/PlayButton/constants';

    type OptionalIconCollection = Partial<IconStateCollection>;

    export function generateIconOverride(
        iconSet: PlayIconSet,
        buttonStates: OptionalIconCollection,
    ): IconMapping {
        return {
            ...iconStateComponentIconMapping,
            [iconSet]: {
                ...iconStateComponentIconMapping[iconSet],
                ...buttonStates,
            },
        };
    }
</script>

<script lang="ts">
    import { PLAY_STATES } from '@amp/web-app-components/src/constants';
    import { onMount } from 'svelte';
    // TODO: revisit all props as part of rdar://86486410 (JMOTW | Play Button Components: "Flavors")
    export let isStandAlone: boolean = false;
    export let isLarge: boolean = false;
    export let isInChin: boolean = false;
    export let isHovered: boolean = false;
    export let isFilled: boolean = false;
    export let playButtonType: 'artworkHover' | null = null;

    /**
     * accessiblity string for playbutton
     * @type {string}
     */
    export let ariaLabel: string = '';
    /**
     * set what icon in the playbutton should be displayed (Play, Buffering, Pause, etc,)
     * @type {IconState}
     */
    export let iconState: IconState = PLAY_STATES.PLAY;
    /**
     * Used to change between different common SVG icon sets
     * @type {PlayIconSet}
     */
    export let iconSet: PlayIconSet = 'platter';
    /**
     * Used to provide custom SVGs to PlayButton states
     * @type {IconMapping}
     */
    export let iconSetOverride: IconMapping | null = null;

    /**
     * Overrides tabindex attribute on <button> element
     * @type {number}
     */
    export let tabIndex: number | null = null;

    $: iconSets =
        iconSetOverride === null
            ? iconStateComponentIconMapping
            : iconSetOverride;
    $: iconComponent = iconSets[iconSet][iconState];

    export let nodeRef: Function = () => {};

    let node: HTMLElement;

    onMount(() => {
        nodeRef(node);
    });
</script>

<button
    aria-label={ariaLabel}
    class="play-button"
    class:play-button--platter={iconSet === 'platter'}
    class:play-button--standard={iconSet === 'non-platter'}
    class:play-button--artwork-overlay={playButtonType === 'artworkHover'}
    class:is-stand-alone={isStandAlone}
    class:is-in-chin={isInChin}
    class:is-large={isLarge}
    class:is-hovered={isHovered}
    class:is-filled={isFilled}
    tabindex={tabIndex}
    data-testid="play-button"
    on:click|preventDefault|stopPropagation
    bind:this={node}
>
    <svelte:component
        this={iconComponent}
        class={`icon ${iconState}-svg`}
        data-testid={`${iconState}-icon`}
        aria-hidden="true"
        {iconState}
    />
</button>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'amp/stylekit/core/mixins/materials' as *;
    @use 'amp/stylekit/core/mixins/browser-targets' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/absolute-center' as *;

    $stand-alone-button-size: 24px;
    $platter-small-button-size: 30px;
    $non-platter-small-button-size: $global-tracklist-play-button-size;
    $platter-large-button-size: 60px;

    .play-button {
        // This is a fix for a regression that occurred because a global `.icon { display: block; }` was injected into the page. It should be removed once we resolve the root issue behind that.
        // TODO: rdar://99165718 (JMOTW: Investigate how/why global styles are being injected)
        // Then remove `stylelint-disable-next-line` rule above the next `.play-button`, below.
        :global(.icon) {
            display: inline-block;
        }
    }

    // CUSTOM PROPS OVERRIDES
    // --playButtonSize => size of playbutton
    // --playButtonIconColor => Color of icon
    // --playButtonIconHoverColor => color of icon on hover
    // --playButtonCircleHoverColor highlight color of circle (platter only)
    //
    // * playButtonType === 'artworkHover' *
    // --trackLockupArtworkOverlay => overlay behind playbutton
    .play-button--platter {
        // Custom properties are embedded in SVG
        --iconCircleFillBG: var(
            --iconCircleFillBGOverride,
            var(--systemQuaternary)
        );
        --iconFillArrow: var(--playButtonIconColor, white);
        border-radius: 50%;

        :global(.icon) {
            display: inline-block;
            height: var(--playButtonSize, #{$platter-small-button-size});
            width: var(--playButtonSize, #{$platter-small-button-size});
        }

        &.is-stand-alone {
            :global(.icon) {
                height: var(--playButtonSize, #{$platter-small-button-size});
                width: var(--playButtonSize, #{$platter-small-button-size});
            }
        }

        &.is-hovered,
        &:hover,
        &.is-filled,
        &:focus-visible {
            // Custom properties are embedded in SVG
            --iconCircleFillBG: var(
                --playButtonCircleHoverColor,
                var(--keyColor, black)
            );
            --iconFillArrow: var(--playButtonIconHoverColor, white);
        }
    }

    .play-button--standard {
        :global(.icon) {
            height: var(--playButtonSize, #{$non-platter-small-button-size});
            width: var(--playButtonSize, #{$non-platter-small-button-size});
        }

        &.is-stand-alone {
            :global(.icon) {
                height: var(
                    --playButtonSize,
                    #{$non-platter-small-button-size}
                );
                width: var(--playButtonSize, #{$non-platter-small-button-size});
            }
        }

        &.is-hovered,
        &:hover,
        &:focus-visible,
        &:focus-within {
            --playButtonIconColor: var(
                --playButtonIconHoverColor,
                var(--keyColor, black)
            );
        }
    }

    // stylelint-disable-next-line no-duplicate-selectors
    .play-button {
        // Custom properties are embedded in SVG
        --nonPlatterIconFill: var(--playButtonIconColor, white);
        --playingBarColor: var(--nonPlatterIconFill, white);
        position: relative;
        pointer-events: auto;
        line-height: 0; // Remove potential white space from focus ring
        z-index: var(--z-default);
        // TODO: rdar://86486410 (JMOTW | Play Button Components: "Flavors")
        // TODO: (UXD) Make frosted button "frostier".  Styling here is temporary
        //       until the radar above can better decouple styling from functionality.
        &.is-in-chin {
            /* width: 30px;
            height: 30px; */
            position: absolute;
            bottom: 20px;
            top: auto;
            opacity: 0;
            transition: var(--global-transition);
            z-index: calc(var(--z-default) + 1);
            inset-inline-start: auto;
            inset-inline-end: 12px;

            :global(.horizontal-poster-lockup:hover) &,
            :global(.horizontal-poster-lockup:focus-visible) &,
            :global(.horizontal-poster-lockup:focus-within) & {
                opacity: 1;
            }
        }

        &.is-large {
            :global(.icon) {
                height: var(--playButtonSize, #{$platter-large-button-size});
                width: var(--playButtonSize, #{$platter-large-button-size});
            }
        }

        //
        // Radial Progress Player
        //
        :global(.radial-progress-player) {
            border-radius: 50%;
            display: inline-block;
            height: var(--playButtonSize, #{$platter-small-button-size});
            width: var(--playButtonSize, #{$platter-small-button-size});
        }

        :global(.radial-progress-player__play-glyph) {
            fill: var(--radial-progress-play-glyph-fill, white);
        }

        :global(.radial-progress-player--progress-indicator) {
            margin: 0 auto;
            // width: 100%;
            // height: 30px;
            pointer-events: none;

            @include absolute-center;
        }

        :global(.radial-progress-player__track) {
            stroke-width: 1.83;
            stroke-dasharray: 75;
            stroke: var(
                --radial-progress-play-track-stroke,
                #{rgba(255, 255, 255, 0.45)}
            );
        }

        :global(.radial-progress-player__indicator) {
            transition: stroke-dashoffset 1s linear;
            stroke-width: 1.83;
            stroke-dasharray: 75;
            stroke: var(
                --radial-progress-play-indicator-stroke,
                #{rgba(255, 255, 255, 1)}
            );
        }

        :global(.radial-progress-player__buffering) {
            fill: var(
                --radial-progress-buffering-fill,
                #{rgba(255, 255, 255, 0.45)}
            );
            transform-origin: center;
            transform: none;
            animation: buffering-spinner 1s linear infinite;
        }

        :global(.radial-progress-player__pause-glyph) {
            fill: var(--radial-progress-pause-glyph-fill, white);
        }
    }

    .play-button--platter:not(.play-button--artwork-overlay) {
        // Collapse white space
        line-height: 0;
        display: block;

        :global(.icon) {
            border-radius: 50%;

            @include system-standard-thin-material;
        }

        &.is-hovered,
        &:hover,
        &.is-filled,
        &:focus-visible {
            :global(.icon) {
                // Suppress material hairline from bleeding out from behind hovered platter
                background-color: transparent;
                backdrop-filter: none;
            }
        }
    }

    .play-button--artwork-overlay {
        --nonPlatterIconFill: white;
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        border-radius: inherit;
        background-color: var(--trackLockupArtworkOverlay, transparent);
        outline-offset: -4px;
        opacity: var(--play-button-opacity, 0);
        transition: var(--global-transition);
        inset-inline-start: 0;
        z-index: var(--z-default);

        :global(.icon) {
            height: var(--playButtonSize, 27px);
            width: var(--playButtonSize, 27px);
        }
    }

    // We only rotate the inner `path` to avoid spinner wobble from 0.5px track borders
    .play-button :global(.buffer-svg path) {
        transform-origin: center;
        transform: none;
        animation: buffering-spinner 1s linear infinite;
    }

    @keyframes buffering-spinner {
        to {
            transform: rotate(360deg);
        }
    }
</style>
